import { ValidationError } from "sequelize"
import {
    CcLibrarySet,
    CcLibrarySetCreate,
    CcLibrarySetSave,
    CcLibrarySetChangeStatus,
    CcLibrarySetImport,
} from "server/models"
import {
    PUBLISHED, UNPUBLISHED, NONE, VALUE, UNLIMITED, COMPLIANCE, REVIEW,
} from "const"
import { PublishedUnpublished, OrderType } from "client/utils"
import {
    GET, POST, DELETE, sort, search, validate,
} from "client/store/utils"
import {
    LangType,
    UserData,
    PackageSettings,
    AddCcLibrarySetModule,
} from "client/store"
import {
    CcLibrarySetList,
    CcLibrarySetFilters,
    CcLibrarySetValidation,
} from "./types"


export const LoadAllCcLibrarySets = (accountId: number, projectId: number) => (
    GET(`/ccLibrarySet/accountId/${accountId}/projectId/${projectId}`)
    .then((sets: CcLibrarySet[]) => {
        const setList: CcLibrarySetList = {}
        sets.forEach((set) => {
            setList[set.id] = set
        })
        return setList
    })
)

export const LoadCcLibrarySetsReadyForImport = (
    accountId: number,
    targetLevel: number, // 0 - to account level, 1 - to project level
) => (
    GET(`/ccLibrarySet/${targetLevel === 0 ? "readyForImportToAccount" : "readyForImportToProject"}/accountId/${accountId}`)
    .then((sets: CcLibrarySet[]) => {
        const setList: CcLibrarySetList = {}
        sets.forEach((set) => {
            setList[set.id] = set
        })
        return setList
    })
)

export const LoadCcLibrarySetById = (
    setId: number,
    accountId: number,
    projectId: number,
) => (
    GET(`/ccLibrarySet/id/${setId}/accountId/${accountId}/projectId/${projectId}`)
    .then((set: CcLibrarySet) => set)
)

type SaveCcLibrarySetResponse = {
    newSetId: number
    validation: CcLibrarySetValidation
}
export const SaveCcLibrarySet = (
    id: number,
    set: CcLibrarySetCreate,
    maxSetNumber: number,
    lang: LangType,
) => (
    LoadAllCcLibrarySets(set.accountId, set.projectId).then((setList) => {
        // count library sets created by account
        let count = 0
        Object.values(setList).forEach((row: CcLibrarySet) => {
            if (row.ownerAccount === row.accountId) count += 1
        })

        if (!id && maxSetNumber !== null && count >= maxSetNumber) {
            return <SaveCcLibrarySetResponse>{
                newSetId: null,
                validation: <CcLibrarySetValidation>{
                    publicId: lang.maxAccountSetsReached.replace("{0}", maxSetNumber.toString()),
                },
            }
        }

        const data: CcLibrarySetSave = {
            id,
            set,
        }
        return POST(
            "/ccLibrarySet/save",
            JSON.stringify(data),
        ).then((res) => {
            const newSet = res as CcLibrarySet
            const error = res as ValidationError

            return <SaveCcLibrarySetResponse>{
                newSetId: newSet.id || null,
                validation: <CcLibrarySetValidation>validate(error, lang),
            }
        })
    })
)

export const ChangeCcLibrarySetStatus = (set: CcLibrarySet) => {
    const status: PublishedUnpublished = set.status === PUBLISHED ? UNPUBLISHED : PUBLISHED
    const data: CcLibrarySetChangeStatus = {
        id: set.id,
        status,
    }
    return POST(
        "/ccLibrarySet/changeStatus",
        JSON.stringify(data),
    ).then((updatedRows: [number]) => (
        updatedRows && updatedRows.length && updatedRows[0] === 1
            ? status
            : set.status
    ))
}

export const DeleteCcLibrarySet = (setId: number) => (
    DELETE(`/ccLibrarySet/delete/${setId}`)
    .then((removedRows: number) => removedRows)
)

export const SortCcLibrarySets = (
    sets: CcLibrarySet[],
    sortColumn: keyof CcLibrarySet,
    sortOrder: OrderType,
    filters: CcLibrarySetFilters,
) => {
    const setsFiltered = sets.filter((set) => {
        let showRow = true
        if (filters.imported && set.ownerAccount === set.accountId) showRow = false
        else if (filters.created && set.ownerAccount !== set.accountId) showRow = false
        else if (filters.status && set.status !== filters.status) showRow = false
        else if (filters.search) showRow = search(filters.search, set)
        return showRow
    })
    setsFiltered.sort((a, b) => sort(a, b, sortColumn, sortOrder))

    const setIds: number[] = []
    setsFiltered.forEach((set) => setIds.push(set.id))

    return setIds
}

export const ImportCcLibrarySet = (
    accountId: number,
    projectId: number,
    set: CcLibrarySet,
) => {
    const data: CcLibrarySetImport = {
        accountId,
        projectId,
        set,
    }

    return POST(
        "/ccLibrarySet/import",
        JSON.stringify(data),
    ).then((newSet) => {
        if (!newSet.id) return null

        const promiseArray: Promise<any>[] = []
        if (projectId) {
            promiseArray.push(
                AddCcLibrarySetModule({
                    setId: newSet.id,
                    module: COMPLIANCE,
                }),
                AddCcLibrarySetModule({
                    setId: newSet.id,
                    module: REVIEW,
                }),
            )
        }
        return Promise.all(promiseArray).then(() => newSet)
    })
}

export const CopyDefaultCcLibrarySets = (
    accountId: number,
    projectId: number,
    defaultLibraryAssigned: boolean,
) => (
    GET(`/ccLibrarySet/default/accountId/${defaultLibraryAssigned ? accountId : 1}`)
    .then((sets: CcLibrarySet[]) => {
        const promiseArray: Promise<any>[] = []
        sets.forEach((set) => {
            promiseArray.push(
                ImportCcLibrarySet(accountId, projectId, set),
            )
        })

        return Promise.all(promiseArray).then((res) => res)
    })
)

export const GetMaxCcLibrarySets = (
    packageSettings: PackageSettings,
    userData: UserData,
    accountSetNumber?: number,
    librarySetNumber?: number,
    lang?: LangType,
) => {
    const {
        ccAccountComplianceSets,
        ccAccountComplianceSetsValue,

        ccLibraryComplianceSets,
        ccLibraryComplianceSetsValue,
    } = packageSettings

    // number of sets created by account
    let maxAccountSets = 0
    if (ccAccountComplianceSets === NONE) maxAccountSets = 0
    if (ccAccountComplianceSets === VALUE && ccAccountComplianceSetsValue) maxAccountSets = ccAccountComplianceSetsValue
    if (ccAccountComplianceSets === UNLIMITED) maxAccountSets = null
    if (userData.accountId === 1) maxAccountSets = null

    // number of imported sets
    let maxLibrarySets = 0
    if (ccLibraryComplianceSets === NONE) maxLibrarySets = 0
    if (ccLibraryComplianceSets === VALUE && ccLibraryComplianceSetsValue) maxLibrarySets = ccLibraryComplianceSetsValue
    if (ccLibraryComplianceSets === UNLIMITED) maxLibrarySets = null
    if (userData.accountId === 1) maxLibrarySets = 0

    // feedbacks
    let feedbackAccountSets: string = ""
    let feedbackLibrarySets: string = ""
    if (lang) {
        if (maxAccountSets === null) {
            feedbackAccountSets = lang.maxAccountSetsUnlimited
        } else {
            if (accountSetNumber < maxAccountSets) feedbackAccountSets = lang.maxAccountSets
            if (accountSetNumber === maxAccountSets) feedbackAccountSets = lang.maxAccountSetsReached
            if (accountSetNumber > maxAccountSets) feedbackAccountSets = lang.maxAccountSetsExceeded
            feedbackAccountSets = feedbackAccountSets.replace("{0}", maxAccountSets.toString())
        }

        if (maxLibrarySets === null) {
            feedbackLibrarySets = lang.maxLibrarySetsUnlimited
        } else {
            if (librarySetNumber < maxLibrarySets) feedbackLibrarySets = lang.maxLibrarySets
            if (librarySetNumber === maxLibrarySets) feedbackLibrarySets = lang.maxLibrarySetsReached
            if (librarySetNumber > maxLibrarySets) feedbackLibrarySets = lang.maxLibrarySetsExceeded
            feedbackLibrarySets = feedbackLibrarySets.replace("{0}", maxLibrarySets.toString())
        }
    }

    return {
        maxAccountSets,
        maxLibrarySets,
        feedbackAccountSets,
        feedbackLibrarySets,
    }
}

export const IsOwnerCcLibrarySet = (
    projectId: number,
    set: CcLibrarySet,
    userData: UserData,
) => (
    !projectId
    && set
    && set.ownerAccount === userData.accountId
    && set.creator === userData.userId
)

export const IsLockedCcLibrarySet = (
    projectId: number,
    set: CcLibrarySet,
    userData: UserData,
) => (
    projectId
        ? set && set.locked
        : set && set.ownerAccount !== userData.accountId && set.locked
)
